import { useEffect } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { useLocation, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '../../components/UI';
import { Input } from '../../components/form';

import { EmailUsernameValidator } from '../../components/form/Input/validators/EmailUsernameValidator';

import SignUpWithGoogle from './SignUpWithGoogle';
import SignUpWithTwitter from './SignUpWithTwitter';

import { useSignUpContext } from './SignUpContext';
import { trackEvent } from '../../helpers/analytics';

const SignUpInitialForm = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useSignUpContext();

  const { pathname } = useLocation();
  const { referralCode } = useParams();

  const isReferral = referralCode && pathname.indexOf('referralCode') !== -1;

  useEffect(() => {
    if (isReferral) {
      const ogUrl = document.createElement('meta');
      const ogDescription = document.createElement('meta');

      ogUrl.setAttribute('property', 'og:url');
      ogUrl.content = window.location.hostname + pathname;

      ogDescription.setAttribute('property', 'og:description');
      ogDescription.content = t('signUp.referralOgDescription');

      document.getElementsByTagName('head')[0].appendChild(ogUrl);
      document.getElementsByTagName('head')[0].appendChild(ogDescription);
    }
  }, [isReferral, pathname]);

  const formik = useFormik({
    initialValues: {
      email: '',
      referralCode: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      dispatch({
        type: 'SET_EMAIL',
        payload: values.email,
      });
      dispatch({
        type: 'SET_STEP',
        payload: 'FINAL_FORM',
      });

      trackEvent('signup_email_initiated');

      formik.setSubmitting(false);
    },
  });

  return (
    <>
      <h1
        className={
          'text-black text-xl font-semibold text-center dark:text-white'
        }
      >
        {t('signUp.form.title')}
      </h1>
      {isReferral && (
        <div
          className={'text-black text-md mb-4 font-semibold dark:text-white'}
        >
          <Trans i18nKey="signUp.referralFormatted" referralCode={referralCode}>
            <p>first_line</p>
            <p>second_line</p>
            <p>{{ referralCode }}</p>
          </Trans>
        </div>
      )}
      <>
        <SignUpWithGoogle />
        <SignUpWithTwitter />
        <div className={'mt-8 w-full flex items-center'}>
          <div className={'border-b-1 border-grey-30 flex-grow-3'} />
          <div
            className={
              'flex-grow-1 flex justify-center text-grey-30 text-sm uppercase'
            }
          >
            {t('signUp.form.or')}
          </div>
          <div className={'border-b-1 border-grey-30 flex-grow-3'} />
        </div>
      </>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="mt-8">
            <Input
              name="email"
              formik={formik}
              id="email"
              validate={EmailUsernameValidator}
              label={t('signUp.form.label.email')}
              size="full"
              type="text"
              placeholder={t('signUp.form.placeholder.email')}
              errorMessage={
                formik.errors.email && formik.touched.email
                  ? t('signUp.form.error.email.' + formik.errors.email)
                  : ''
              }
            />
          </div>

          <Button
            type={'submit'}
            variant="primary"
            size="full"
            buttonClass={'mt-5'}
            loading={formik.isSubmitting || state.isLoading}
          >
            {t('signUp.form.label.continue')}
          </Button>
          <div className={'mt-8'}>
            <div className={'mt-4 text-center'}>
              <div
                className={
                  'text-sm text-grey-50 cursor-pointer dark:text-grey-30'
                }
              >
                <Trans i18nKey="signUp.form.haveAccount">
                  have_account
                  <Link
                    className={'text-black dark:text-white font-semibold'}
                    to={'/signin'}
                  >
                    login
                  </Link>
                </Trans>
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </>
  );
};
export default SignUpInitialForm;
